import React, { FC, useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { KTSVG } from '../../../../_metronic/helpers'
import { Vessel, VesselLandingResponse, VesselGeneralInfo, VesselParticulars } from '../core/_models'
import ViewVesselModal from './ViewVesselModal'
import ViewShipParticularsModal from './ViewShipParticularsModal'
import {
  getVesselLanding,
  getVesselImageUrl,
  getGeneralInfoOfVessel,
  getVesselParticulars,
} from '../core/_requests'
import MapView from '../../../pages/dashboard/components/MapView'
import type { Vessel as DashVessel } from '../../../pages/dashboard/core/_models'
import { getVesselDetailLabel, getVesselDetailMode } from '../core/vesselDetailMode'
import { validateCoordinateString } from '../../../shared/utils/coordinateParsing'

const MaximizeIcon: FC<{ className?: string }> = ({ className }) => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' className={className}>
    <rect x='3.5' y='3.5' width='17' height='17' rx='3' stroke='currentColor' strokeWidth='1.5' />
  </svg>
)

const RestoreIcon: FC<{ className?: string }> = ({ className }) => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' className={className}>
    <path
      d='M15 3.5H9C6.5 3.5 4.5 5.5 4.5 8V14'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <rect x='8.5' y='8.5' width='11' height='11' rx='3' stroke='currentColor' strokeWidth='1.5' />
  </svg>
)

const ExpandIcon: FC<{ className?: string }> = ({ className }) => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' className={className}>
    <path
      d='M4 8V4H8'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M20 8V4H16'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4 16V20H8'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M20 16V20H16'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CollapseIcon: FC<{ className?: string }> = ({ className }) => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' className={className}>
    <path
      d='M8 4V8H4'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16 4V8H20'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M8 20V16H4'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16 20V16H20'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

interface Props {
  isOpen: boolean
  onClose: () => void
  vesselData?: Vessel | null
  vesselId?: number | null
}

const VesselLandingModal: FC<Props> = ({ isOpen, onClose, vesselData, vesselId }) => {
  const [isQ88ModalOpen, setIsQ88ModalOpen] = useState(false)
  const [isMaximized, setIsMaximized] = useState(false)
  const [isMapExpanded, setIsMapExpanded] = useState(false)
  const [shareStatus, setShareStatus] = useState<'idle' | 'loading' | 'copied'>('idle')
  const [landing, setLanding] = useState<VesselLandingResponse | null>(null)
  const [loadingLanding, setLoadingLanding] = useState(false)
  const [errorLanding, setErrorLanding] = useState<string | null>(null)

  const [vesselImgUrl, setVesselImgUrl] = useState<string | null>(null)
  const [imgLoading, setImgLoading] = useState<boolean>(false)
  const [imgError, setImgError] = useState<string | null>(null)
  const [generalInfo, setGeneralInfo] = useState<VesselGeneralInfo | null>(null)
  const [particulars, setParticulars] = useState<VesselParticulars | null>(null)

  const resolvedVesselId = vesselId ?? vesselData?.id ?? null
  const resolvedVesselData = landing?.vesseldto ?? vesselData ?? null
  const vesselDetailMode = getVesselDetailMode(resolvedVesselData?.vesselType ?? null)
  const vesselDetailButtonLabel = getVesselDetailLabel(
    resolvedVesselData?.vesselType ?? null,
    'view'
  )

  const text = (v: any) =>
    v !== null && v !== undefined && String(v).trim() !== '' ? String(v) : '—'
  const mt = (v: any) => (v ? `${v} MT` : '—')
  const nm = (v: any) => (v ? `${v} NM` : '—')
  const kn = (v: any) => (v ? `${v} kn` : '—')
  const deg = (v: any) => (v ? `${v}°` : '—')
  const getYear = (v: any) => {
    if (!v) return '—'
    const year = new Date(v).getFullYear()
    return isNaN(year) ? String(v) : String(year)
  }

  const landingRequestIdRef = useRef(0)

  const fetchLandingData = useCallback(async () => {
    if (resolvedVesselId == null) return
    const requestId = ++landingRequestIdRef.current
    setLoadingLanding(true)
    setErrorLanding(null)

    try {
      const data = await getVesselLanding(resolvedVesselId)
      if (requestId !== landingRequestIdRef.current) return
      setLanding(data)
    } catch (e: any) {
      if (requestId !== landingRequestIdRef.current) return
      console.error('Failed to load vessel landing data:', e)
      const msg = showErrorToast(e, 'Failed to load vessel landing data.')
      setErrorLanding(msg || e?.message || 'Failed to load landing data')
    } finally {
      if (requestId === landingRequestIdRef.current) {
        setLoadingLanding(false)
      }
    }
  }, [resolvedVesselId])

  useEffect(() => {
    if (!isOpen || resolvedVesselId == null) {
      landingRequestIdRef.current++
      setLanding(null)
      setErrorLanding(null)
      setLoadingLanding(false)
      return
    }
    setLanding(null)
    fetchLandingData()

    return () => {
      landingRequestIdRef.current++
    }
  }, [isOpen, resolvedVesselId, fetchLandingData])

  // Fetch additional vessel general info (for GRT/NRT)
  useEffect(() => {
    if (!isOpen || resolvedVesselId == null) {
      setGeneralInfo(null)
      setParticulars(null)
      return
    }
    let ignore = false
    setGeneralInfo(null)
    setParticulars(null)

    getGeneralInfoOfVessel(resolvedVesselId)
      .then((data) => {
        if (!ignore) setGeneralInfo(data)
      })
      .catch(() => {
        if (!ignore) setGeneralInfo(null)
      })

    getVesselParticulars(resolvedVesselId)
      .then((data) => {
        if (!ignore) setParticulars(data)
      })
      .catch(() => {
        if (!ignore) setParticulars(null)
      })

    return () => {
      ignore = true
    }
  }, [isOpen, resolvedVesselId])

  // Fetch vessel image (single image)
  useEffect(() => {
    if (!isOpen || resolvedVesselId == null) return
    let ignore = false
    let objectUrlToRevoke: string | null = null

    setImgLoading(true)
    setImgError(null)
    setVesselImgUrl(null)

    getVesselImageUrl(resolvedVesselId)
      .then((url) => {
        if (ignore) return
        if (url) {
          setVesselImgUrl(url)
          // We can’t know for sure if it's an object URL we created,
          // but if it starts with "blob:", clean up on unmount.
          if (url.startsWith('blob:')) objectUrlToRevoke = url
        } else {
          setVesselImgUrl(null)
        }
      })
      .catch((e) => {
        if (!ignore) setImgError('Failed to load vessel image')
      })
      .finally(() => {
        if (!ignore) setImgLoading(false)
      })

    return () => {
      ignore = true
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke)
      }
    }
  }, [isOpen, resolvedVesselId])

  if (!isOpen) return null
  if (resolvedVesselId == null) return null

  const handleToggleMaximize = () => {
    setIsMaximized(!isMaximized)
  }

  const handleShare = async () => {
    if (shareStatus !== 'idle') return

    setShareStatus('loading')

    await new Promise((resolve) => setTimeout(resolve, 1000))
    const token = Math.random().toString(36).substring(2, 12)

    const publicUrl = `${window.location.origin}/public/vessel/${resolvedVesselId || '12345'
      }-${token}`

    try {
      await navigator.clipboard.writeText(publicUrl)
      setShareStatus('copied')
      toast.success('Share link copied to clipboard!')
    } catch (err) {
      console.error('Failed to copy link: ', err)
      toast.warn('Failed to copy link to clipboard.')
      setShareStatus('idle')
    }

    setTimeout(() => {
      setShareStatus('idle')
    }, 3000)
  }

  const handleOpenVesselDetailModal = () => {
    if (!resolvedVesselId || resolvedVesselId <= 0) {
      toast.warn('Vessel details are not available.')
      return
    }
    setIsQ88ModalOpen(true)
  }

  const q88VesselData = (resolvedVesselData ??
    vesselData ?? {
    id: resolvedVesselId ?? 0,
    fleet_name: 'Ocean Voyager',
    imoNumber: '',
  }) as { id: number; fleet_name?: string; imoNumber?: string }

  // Build a single "dashboard Vessel" from the modal's data (typed to Dashboard model)
  const singleVesselMarker: DashVessel = {
    id: resolvedVesselId || 0,
    fleet_name: resolvedVesselData?.fleet_name ?? 'Ocean Voyager',
    imoNumber: resolvedVesselData?.imoNumber ?? '',
    vesselType: resolvedVesselData?.vesselType ?? '',
    status: landing?.status ?? 'Unknown',
    port: landing?.nextPort ?? '',
    eta: landing?.latestVoyage?.eta ?? '',
    // coords optional; MapView will fetch tracking by id
  }

  const handleWhatsAppClick = (phoneNumber: string) => {
    const cleanedNumber = phoneNumber.replace(/[^0-9]/g, '')
    window.open(`https://wa.me/${cleanedNumber}`, '_blank')
  }

  return (
    <>
      <div
        className='modal-overlay'
        onClick={isMapExpanded ? undefined : onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className='modal-content'
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: isMaximized ? '100%' : '115rem',
            width: isMaximized ? '100%' : '90%',
            maxHeight: isMaximized ? '100%' : '90vh',
            height: isMaximized ? '100%' : 'auto',
            overflowY: 'auto',
            backgroundColor: '#f8f9fa',
            borderRadius: isMaximized ? '0' : '8px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            className='d-flex justify-content-between align-items-center bg-white'
            style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}
          >
            <h5 className='m-0'>
              Vessel Details - {resolvedVesselData?.fleet_name || 'Ocean Voyager'}
            </h5>
            <div className='d-flex align-items-center gap-2'>
              <button className='btn btn-sm btn-light' onClick={handleToggleMaximize}>
                {isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
              </button>
              <button className='btn btn-sm btn-light' onClick={onClose}>
                <KTSVG path='/media/map/x.svg' className='svg-icon-2x svg-icon-gray-700' />
              </button>
            </div>
          </div>

          <div style={{ padding: '0', flex: 1, overflowY: 'auto' }}>
            {/* HEADER SUMMARY BAR */}
            <div className='bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between'>
              {/* Left: vessel name + meta */}
              <div className='me-3'>
                <h3 className='fw-bold mb-1'>
                  {text(resolvedVesselData?.fleet_name ?? 'Ocean Voyager')}
                </h3>
                <p className='text-muted mb-0'>
                  IMO: {text(resolvedVesselData?.imoNumber ?? '9123456')} | Flag:{' '}
                  {text(resolvedVesselData?.flag ?? 'Panama')} | Type:{' '}
                  {text(
                    (resolvedVesselData?.vesselType ?? '')?.replace(/_/g, ' ') || 'Bulk Carrier'
                  )}
                </p>
              </div>

              {/* Middle: small banner image (shown from md and up) */}
              <div className='d-none d-md-block mx-3 flex-grow-1' style={{ maxWidth: 360 }}>
                <div
                  className='rounded d-flex align-items-center justify-content-center'
                  style={{
                    height: 80,
                    backgroundImage: vesselImgUrl ? `url("${vesselImgUrl}")` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid #e9ecef',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Loading / error / fallback overlay */}
                  {imgLoading && (
                    <div
                      className='w-100 h-100 d-flex align-items-center justify-content-center'
                      style={{ background: 'rgba(255,255,255,0.6)' }}
                    >
                      <span className='text-muted small'>Loading image…</span>
                    </div>
                  )}
                  {!imgLoading && !vesselImgUrl && (
                    <div className='w-100 h-100 d-flex align-items-center justify-content-center bg-light'>
                      <span className='text-muted small'>
                        {imgError ? 'No image available' : 'No image'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: actions */}
              <div className='d-flex gap-2 ms-3 flex-shrink-0'>
                {/* <button className="btn btn-sm btn-primary" onClick={handleShare} disabled={shareStatus !== 'idle'}>
      {shareStatus === 'idle' && 'Share'}
      {shareStatus === 'loading' && 'Generating...'}
      {shareStatus === 'copied' && 'Link Copied!'}
    </button> */}
                <button className='btn btn-sm btn-primary' onClick={handleOpenVesselDetailModal}>
                  {vesselDetailButtonLabel}
                </button>
              </div>
            </div>

            {loadingLanding && (
              <div className='bg-warning text-dark small px-4 py-2 border-bottom d-flex align-items-center gap-2'>
                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                <span>Loading latest vessel data…</span>
              </div>
            )}
            {errorLanding && (
              <div className='bg-danger text-white small px-4 py-2 border-bottom d-flex justify-content-between align-items-center'>
                <span>Failed to load vessel data: {errorLanding}</span>
                <button
                  type='button'
                  className='btn btn-sm btn-light py-0 px-2 text-danger fw-semibold'
                  onClick={fetchLandingData}
                  disabled={loadingLanding}
                >
                  Retry
                </button>
              </div>
            )}

            {/* SUMMARY CARDS */}
            <div className='bg-white border-bottom p-3'>
              <div className='row g-2'>
                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Tonnage</p>
                    <div className='d-flex justify-content-between small'>
                      <span className='text-muted'>DWT:</span>
                      <span className='fw-bold'>{mt(resolvedVesselData?.dwt)}</span>
                    </div>
                    <div className='d-flex justify-content-between small'>
                      <span className='text-muted'>GRT:</span>
                      <span className='fw-bold'>
                        {text(
                          vesselDetailMode === 'Q88'
                            ? generalInfo?.grossTonnage
                            : particulars?.grossTonnage
                        )}
                      </span>
                    </div>
                    <div className='d-flex justify-content-between small'>
                      <span className='text-muted'>NRT:</span>
                      <span className='fw-bold'>
                        {text(
                          vesselDetailMode === 'Q88'
                            ? generalInfo?.netTonnage
                            : particulars?.netTonnage
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-lg col-md-3 col-6">
                  <div className="border rounded p-2 h-100">
                    <p className="text-muted small mb-1">Build Year</p>
                    <h6 className="fw-bold mb-0">
                      {(() => {
                        const dateStr = vesselDetailMode === 'Q88' ? generalInfo?.dateDelivered : particulars?.deliveredDate;
                        if (!dateStr) return '—';
                        const date = new Date(dateStr);
                        return isNaN(date.getFullYear()) ? '—' : date.getFullYear().toString();
                      })()}
                    </h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Vessel Type</p>
                    <h6 className='fw-bold mb-0'>
                      {text((resolvedVesselData?.vesselType ?? '')?.replace(/_/g, ' ') || '—')}
                    </h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Status</p>
                    <h6 className='fw-bold mb-0'>{text(landing?.status)}</h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Call Sign</p>
                    <h6 className='fw-bold mb-0'>{text(resolvedVesselData?.call_sign ?? '—')}</h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>MMSI</p>
                    <h6 className='fw-bold mb-0'>{text(resolvedVesselData?.mmsi ?? '—')}</h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Last Port</p>
                    <h6 className='fw-bold mb-0'>{text(landing?.lastPort ?? '—')}</h6>
                  </div>
                </div>

                <div className='col-lg col-md-3 col-6'>
                  <div className='border rounded p-2 h-100'>
                    <p className='text-muted small mb-1'>Next Port</p>
                    <h6 className='fw-bold mb-0'>{text(landing?.nextPort ?? '—')}</h6>
                  </div>
                </div>
              </div>
            </div>

            {/* Four boxes side-by-side (single row) */}
            <div className='row g-0'>
              {/* 1) Navigation */}
              <div className='col-12 col-md-6 col-xl-4'>
                <div className='bg-white p-3 border h-100'>
                  <h5 className='text-primary fw-bold'>Navigation & Command</h5>
                  <div className='mt-2'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <h6 className='mb-0'>Current Position</h6>
                      <button
                        className='btn btn-sm btn-outline-primary'
                        onClick={() => setIsMapExpanded(!isMapExpanded)}
                        aria-label={isMapExpanded ? 'Collapse Map' : 'Expand Map'}
                        title={isMapExpanded ? 'Collapse Map' : 'Expand Map'}
                      >
                        {isMapExpanded ? <CollapseIcon /> : <ExpandIcon />}
                      </button>
                    </div>

                    <div
                      className='rounded mt-2'
                      style={{ height: 160, border: '1px solid #e9ecef', overflow: 'hidden' }}
                    >
                      <MapView vesselMarkers={[singleVesselMarker]} height={160} compact />
                    </div>

                    <div className='mt-2 small'>
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted'>Lat/Long:</span>
                        <span>
                          {text(landing?.latitude)} , {text(landing?.longitude)}
                        </span>
                      </div>
                      {(() => {
                        const latWarn = landing?.latitude
                          ? validateCoordinateString(landing.latitude, 'lat').message
                          : null
                        const lngWarn = landing?.longitude
                          ? validateCoordinateString(landing.longitude, 'lon').message
                          : null
                        if (!latWarn && !lngWarn) return null
                        return (
                          <div
                            className='alert alert-danger py-1 px-2 mb-1'
                            style={{ fontSize: '11px' }}
                          >
                            <div className='fw-bold'>Invalid coordinates</div>
                            {latWarn && <div>{latWarn}</div>}
                            {lngWarn && <div>{lngWarn}</div>}
                          </div>
                        )
                      })()}
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted'>Speed:</span>
                        <span>{kn(landing?.speed)}</span>
                      </div>
                      <div className='d-flex justify-content-between'>
                        <span className='text-muted'>Course:</span>
                        <span>{deg(landing?.course)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2) Communication */}
              <div className='col-12 col-md-6 col-xl-4'>
                <div className='bg-white p-3 border h-100'>
                  <h5 className='text-primary fw-bold'>Communication</h5>

                  <div className='mt-2'>
                    <div className='mb-3'>
                      <h6 className='fw-bold mb-2'>Vessel Contact</h6>
                      <div className='small mb-1'>
                        <span className='text-muted'>Phone:</span>
                        <span className='ms-2'>
                          {(resolvedVesselData?.vesselPhoneNumbers?.length
                            ? resolvedVesselData.vesselPhoneNumbers
                            : resolvedVesselData?.vesselPhoneNumber
                              ? [resolvedVesselData.vesselPhoneNumber]
                              : []
                          ).map((value, index) => (
                            <span className='d-block' key={`phone-${value}-${index}`}>
                              {text(value)}
                              {index === 0 ? ' (Primary)' : ''}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className='small mb-1'>
                        <span className='text-muted'>Email:</span>
                        <span className='ms-2'>
                          {(resolvedVesselData?.vesselEmails?.length
                            ? resolvedVesselData.vesselEmails
                            : resolvedVesselData?.vesselEmailAddress
                              ? [resolvedVesselData.vesselEmailAddress]
                              : []
                          ).map((value, index) => (
                            <span className='d-block' key={`email-${value}-${index}`}>
                              {text(value)}
                              {index === 0 ? ' (Primary)' : ''}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <hr className='h-0.5 border-0 bg-gray-300' />
                  {/* Agent Details */}

                  <h5 className='text-primary fw-bold'>Agent Information</h5>

                  <div className='mt-2'>
                    <div className='mb-3'>
                      {/* <h6 className="fw-bold mb-2">Vessel Contact</h6> */}
                      <div className='small mb-1'>
                        <span className='text-muted'>Agent Name:</span>
                        <span className='ms-2'>{landing?.latestVoyage?.agentName}</span>
                      </div>
                      <div className='small mb-1'>
                        <span className='text-muted'>Agent Contact:</span>
                        <span className='ms-2'>{landing?.latestVoyage?.agentContactNumber}</span>
                      </div>

                      <div className='small mb-1'>
                        <span className='text-muted'>Agent Telephone:</span>
                        <span className='ms-2'>{landing?.latestVoyage?.agentTelephoneNumber}</span>
                      </div>

                      <div className='small mb-1'>
                        <span className='text-muted'>Agent Fax:</span>
                        <span className='ms-2'>{landing?.latestVoyage?.agentFaxAddress}</span>
                      </div>

                      <div className='small mb-1'>
                        <span className='text-muted'>Agent Email:</span>
                        <span className='ms-2'>{landing?.latestVoyage?.agentEmail}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3) Voyage Information (use latestVoyage.voyageNumber) */}
              <div className='col-12 col-md-6 col-xl-4'>
                <div className='bg-white p-3 border h-100'>
                  <h5 className='text-primary fw-bold'>Voyage Information</h5>
                  <div
                    className='mt-2 p-2 rounded'
                    style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                  >
                    <div className='d-flex justify-content-between small mb-1'>
                      <span className='text-muted'>Voyage:</span>
                      <span>{text(landing?.latestVoyage?.voyageNumber)}</span>
                    </div>
                    <div className='d-flex justify-content-between small mb-1'>
                      <span className='text-muted'>Last Port:</span>
                      <span>{text(landing?.lastPort)}</span>
                    </div>
                    <div className='d-flex justify-content-between small mb-1'>
                      <span className='text-muted'>Next Port:</span>
                      <span>{text(landing?.nextPort)}</span>
                    </div>
                    <div className='d-flex justify-content-between small mb-1'>
                      <span className='text-muted'>Distance:</span>
                      <span>{nm(landing?.distanceObserved)}</span>
                    </div>
                    <div className='d-flex justify-content-between small'>
                      <span className='text-muted'>Cargo:</span>
                      <span>{mt(landing?.cargoWeight)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4) Reserved */}
              {/* <div className="col-12 col-md-6 col-xl-3">
    <div className="bg-white p-3 border h-100 d-flex flex-column align-items-center justify-content-center text-center">
      <KTSVG path="/media/icons/duotune/general/gen045.svg" className="svg-icon-3x text-muted mb-2" />
      <h6 className="text-muted mb-1">Reserved for Future Features</h6>
      <p className="small text-secondary mb-0">This section will be updated</p>
    </div>
  </div> */}
            </div>
          </div>
        </div>
      </div>

      {isQ88ModalOpen && (
        <div style={{ position: 'relative', zIndex: 1060 }}>
          {vesselDetailMode === 'Q88' ? (
            <ViewVesselModal
              isOpen={isQ88ModalOpen}
              onClose={() => setIsQ88ModalOpen(false)}
              vesselData={q88VesselData}
            />
          ) : (
            <ViewShipParticularsModal
              isOpen={isQ88ModalOpen}
              onClose={() => setIsQ88ModalOpen(false)}
              vesselData={resolvedVesselData as Vessel}
            />
          )}
        </div>
      )}

      {/* Full Screen Map Modal */}
      {isMapExpanded && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 1070,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setIsMapExpanded(false)}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              maxWidth: '1400px',
              maxHeight: '900px',
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Map Close Button */}
            <button
              onClick={() => setIsMapExpanded(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                zIndex: 1,
                width: '40px',
                height: '40px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'white',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                color: '#666',
              }}
            >
              ×
            </button>

            {/* Full Screen Map */}
            <div style={{ width: '100%', height: '100%' }}>
              <MapView vesselMarkers={[singleVesselMarker]} height='100%' />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default VesselLandingModal
